import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ElementRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { IconComponent } from '../../atoms/icon/icon.component';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { Router, RouterModule } from '@angular/router';

import {
  ContactItem,
  ContactItemUnit,
} from '../../../shared/interfaces/contact-item.interface';
import { ModalComponent } from '../modal/modal.component';
import {
  CaasComponent,
  CaasEmailItems,
  CaasFormField,
  CaasLinksItems,
  CaasLinksItemsValue,
  CaasPhoneItems,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { LinkComponent } from '../../atoms/link/link.component';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { CapitalizePipe } from '../../capitalize.pipe';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-contact-module',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    RouterModule,
    ModalComponent,
    LinkComponent,
    CapitalizePipe,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './contact-module.component.html',
  styleUrls: ['./contact-module.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class ContactModuleComponent implements OnChanges, AfterViewInit {
  @Input() caasData?: CaasComponent;
  @Input() componentId?: string;
  @Input() headline: string = 'Default Header';
  @Input() groups?: ContactItem[];
  @Input() columns: 'col-12' | 'col-6' = 'col-12';
  @Input() backgroundColor: Colors = Colors.WHITE;

  public isLoading = true;
  public modalContactItem: ContactItem['items'][number] = {
    linkText: '',
    href: '',
    icon: '',
    type: '',
    list: [],
  };

  showModal = false;
  BadgeType = BadgeType;
  Sizes = Sizes;
  isBgWhite = false;
  HrefType = HrefType;

  constructor(
    private readonly caasRoutesService: CaasRoutesService,
    private readonly elRef: ElementRef,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }
    }
  }

  ngAfterViewInit(): void {
    this.checkParentClass();
  }

  checkParentClass() {
    const parentSection = this.elRef.nativeElement.closest('section');
    if (parentSection?.classList.contains('bg-grey')) {
      this.isBgWhite = true;
    } else {
      this.isBgWhite = false;
    }
  }

  setCaasContent() {
    if (!this.caasData?.formData.st_items?.value) {
      console.error('Invalid Caas Data');
      return;
    }
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_header?.value) {
          this.headline = this.caasData.formData.st_header?.value;
        }
      },
      'Assigning Headline',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.componentId = this.caasData?.identifier;
      },
      'Assigning Component ID',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.getCaasColumns();
      },
      'Assigning Caas columns',
      this.errorHandler
    );
    this.groups = this.getCaasGroups();
  }

  getCaasColumns() {
    switch (this.caasData?.formData.st_columns?.value.identifier) {
      case '1':
        this.columns = 'col-12';
        break;
      case '2':
        this.columns = 'col-6';
        break;
      default:
        this.columns = 'col-6';
        break;
    }
  }

  getCaasGroups(): ContactItem[] {
    return (
      this.caasData?.formData?.st_items?.value?.map((contactItem) => {
        let items: ContactItemUnit[] = [];
        safeAssign(
          () => {
            let caasEmailItems: CaasEmailItems = {} as CaasEmailItems;
            let caasPhoneItems: CaasPhoneItems = {} as CaasPhoneItems;
            let caasLinksItems: CaasLinksItems = {} as CaasLinksItems;
            let caasLinksLabel: CaasSimpleField = {} as CaasSimpleField;
            let caasLinksTitle: CaasSimpleField = {} as CaasSimpleField;
            safeAssign(
              () => {
                caasEmailItems =
                  contactItem.formData.st_email_items ?? ({} as CaasEmailItems);
              },
              'Assigning Email Items',
              this.errorHandler
            );
            safeAssign(
              () => {
                caasPhoneItems =
                  contactItem.formData.st_phone_items ?? ({} as CaasPhoneItems);
              },
              'Assigning Phone Items',
              this.errorHandler
            );
            safeAssign(
              () => {
                caasLinksItems =
                  contactItem.formData.st_links_items ?? ({} as CaasLinksItems);
              },
              'Assigning Links Items',
              this.errorHandler
            );
            safeAssign(
              () => {
                caasLinksLabel =
                  contactItem.formData.st_links_label ??
                  ({} as CaasSimpleField);
              },
              'Assigning Links Label',
              this.errorHandler
            );
            safeAssign(
              () => {
                caasLinksTitle =
                  contactItem.formData.st_links_title ??
                  ({} as CaasSimpleField);
              },
              'Assigning Links Title',
              this.errorHandler
            );
            items = this.getGroupsItems(
              caasEmailItems,
              caasPhoneItems,
              caasLinksItems,
              caasLinksLabel,
              caasLinksTitle
            );
          },
          'Assigning Group Items',
          this.errorHandler
        );

        return {
          label: this.getGroupsLabel(
            contactItem.formData.st_header ?? {
              fsType: '',
              name: '',
              value: '',
            },
            contactItem.formData.st_subheader ?? {
              fsType: '',
              name: '',
              value: '',
            }
          ),
          items: items || [],
        };
      }) ?? []
    );
  }

  getGroupsLabel(
    headline: CaasSimpleField,
    openingHours: CaasSimpleField
  ): string {
    if (openingHours.value) {
      return `<span><strong>${headline.value}</strong><br>${openingHours.value}</span>`;
    }
    return `<span><strong>${headline.value}</strong></span>`;
  }

  getGroupsItems(
    email: CaasEmailItems,
    phone: CaasPhoneItems,
    link: CaasLinksItems,
    linkLabel: CaasSimpleField,
    linkTitle: CaasSimpleField
  ): ContactItem['items'] {
    let items: ContactItem['items'] = [];
    safeAssign(
      () => {
        items = this.setEmailItems(email, items);
      },
      'Assigning Email Items',
      this.errorHandler
    );
    safeAssign(
      () => {
        items = this.setPhoneItems(phone, items);
      },
      'Assigning Phone Items',
      this.errorHandler
    );
    safeAssign(
      () => {
        items = this.setLinkItems(linkLabel, linkTitle, link, items);
      },
      'Assigning Link Items',
      this.errorHandler
    );
    return items;
  }

  setEmailItems(
    email: CaasEmailItems,
    items: ContactItem['items']
  ): ContactItem['items'] {
    if (email.value?.length > 0) {
      if (email.value.length === 1) {
        let linkText = '';
        let href = 'mailto:';
        let title: string | undefined;
        safeAssign(
          () => {
            linkText = email.value[0].formData.lt_link_email.value ?? '';
          },
          'Assigning Email Link Text',
          this.errorHandler
        );
        safeAssign(
          () => {
            href = `mailto:${email.value[0].formData.lt_link_email.value}`;
          },
          'Assigning Email Link Href',
          this.errorHandler
        );
        safeAssign(
          () => {
            title = email?.value[0]?.formData?.lt_link_title?.value ?? '';
          },
          'Assigning Email Link Title',
          this.errorHandler
        );
        items = [
          ...items,
          {
            linkText: linkText,
            href: href,
            title: title,
            icon: 'envelope',
            type: 'external',
          },
        ];
      } else {
        let count: number | undefined;
        safeAssign(
          () => {
            count = email.value.length; // Add the count property
          },
          'Assigning Count',
          this.errorHandler
        );
        items = [
          ...items,
          {
            linkText: 'Email senden',
            modalTitle: 'E-Mail auswählen:',
            href: '',
            title: 'Email senden',
            list: email.value.map((emailItem) => {
              let linkText = '';
              let linkLabel = '';
              let href = 'mailto:';
              let title = '';
              safeAssign(
                () => {
                  linkText = emailItem.formData.lt_link_email.value ?? '';
                },
                'Assigning LinkText',
                this.errorHandler
              );
              safeAssign(
                () => {
                  linkLabel = emailItem?.formData?.lt_link_label?.value ?? '';
                },
                'Assigning LinkLabel',
                this.errorHandler
              );
              safeAssign(
                () => {
                  href = `mailto:${emailItem.formData.lt_link_email.value}`;
                },
                'Assigning Link Href',
                this.errorHandler
              );
              safeAssign(
                () => {
                  title = emailItem?.formData?.lt_link_title?.value ?? '';
                },
                'Assigning Link Title',
                this.errorHandler
              );
              return {
                linkText: linkText,
                linkLabel: linkLabel,
                href: href,
                type: 'external',
                title: title,
              };
            }),
            icon: 'envelope',
            type: 'external',
            count: count, // Add the count property
          },
        ];
      }
    }
    return items;
  }

  setPhoneItems(
    phone: CaasPhoneItems,
    items: ContactItem['items']
  ): ContactItem['items'] {
    if (phone.value?.length > 0) {
      if (phone.value.length === 1) {
        let linkText = '';
        let href = 'tel:';
        let title: string | undefined;
        safeAssign(
          () => {
            linkText = phone.value[0].formData.lt_link_phone.value ?? '';
          },
          'Assigning linkText',
          this.errorHandler
        );
        safeAssign(
          () => {
            href = `tel:${phone.value[0].formData.lt_link_phone.value}`;
          },
          'Assigning Phone Link Href',
          this.errorHandler
        );
        safeAssign(
          () => {
            title = phone?.value[0]?.formData?.lt_link_title?.value ?? '';
          },
          'Assigning Phone Link Title',
          this.errorHandler
        );
        items = [
          ...items,
          {
            linkText: linkText,
            href: href,
            title: title,
            icon: 'phone',
            type: 'external',
          },
        ];
      } else {
        let count: number | undefined;
        safeAssign(
          () => {
            count = phone.value.length;
          },
          'Assigning Phone Count',
          this.errorHandler
        );
        items = [
          ...items,
          {
            linkText: 'Anrufen',
            modalTitle: 'Telefonnummer auswählen',
            href: '',
            title: 'Anrufen',
            list: phone.value.map((phoneItem) => {
              let linkText = '';
              let linkLabel = '';
              let href = 'tel:';
              let title = '';
              safeAssign(
                () => {
                  linkText = phoneItem.formData.lt_link_phone.value ?? '';
                },
                'Assigning Phone Link Text',
                this.errorHandler
              );
              safeAssign(
                () => {
                  linkLabel = phoneItem?.formData?.lt_link_label?.value ?? '';
                },
                'Assigning Phone Link Label',
                this.errorHandler
              );
              safeAssign(
                () => {
                  href = `tel:${phoneItem.formData.lt_link_phone.value}`;
                },
                'Assigning Phone Link Href',
                this.errorHandler
              );
              safeAssign(
                () => {
                  title = phoneItem?.formData?.lt_link_title?.value ?? '';
                },
                'Assigning Phone Link Title',
                this.errorHandler
              );
              return {
                linkText: linkText,
                linkLabel: linkLabel,
                href: href,
                type: 'external',
                title: title,
              };
            }),
            icon: 'phone',
            type: 'external',
            count: count, // Add the count property
          },
        ];
      }
    }
    return items;
  }

  setLinkItems(
    linkLabel: CaasSimpleField,
    linkTitle: CaasSimpleField,
    link: CaasLinksItems,
    items: ContactItem['items']
  ): ContactItem['items'] {
    if (link.value?.length > 0) {
      if (link.value.length === 1) {
        let linkItem: CaasLinksItemsValue | undefined;
        let href = '/404';
        let fragment = '';
        let linkText = '';
        let title = '';
        let target: string | undefined;
        let type: string | undefined;
        safeAssign(
          () => {
            linkItem = link.value[0];
          },
          'Assigning Link Item',
          this.errorHandler
        );
        safeAssign(
          () => {
            if (linkItem) {
              href = this.getCaasLinkHref(linkItem);
            }
          },
          'Assigning Link Href',
          this.errorHandler
        );

        safeAssign(
          () => {
            fragment = linkItem?.formData?.st_anchor_name?.value ?? '';
          },
          'Assigning Link Fragment',
          this.errorHandler
        );
        safeAssign(
          () => {
            linkText = linkItem?.formData?.lt_link_label?.value ?? '';
          },
          'Assigning Link Text',
          this.errorHandler
        );
        safeAssign(
          () => {
            title = linkItem?.formData?.lt_link_title?.value ?? '';
          },
          'Assigning title',
          this.errorHandler
        );
        safeAssign(
          () => {
            if (linkItem?.formData?.lt_link_type) {
              target = this.getCaasLinkTarget(linkItem.formData.lt_link_type);
            }
          },
          'Assigning Link Target',
          this.errorHandler
        );
        safeAssign(
          () => {
            type = linkItem?.formData?.lt_link_type?.value?.identifier;
          },
          'Assigning Link Type',
          this.errorHandler
        );
        return [
          ...items,
          {
            linkText: linkText,
            href,
            fragment,
            title: title,
            icon: 'globe',
            target: target,
            type: type,
          },
        ];
      } else {
        let linkText = '';
        let modalTitle = '';
        let title = '';
        safeAssign(
          () => {
            linkText = linkLabel?.value ?? '';
          },
          'Assigning Link Text',
          this.errorHandler
        );
        safeAssign(
          () => {
            modalTitle = linkTitle?.value ?? '';
          },
          'Assigning Modal Title',
          this.errorHandler
        );
        safeAssign(
          () => {
            title = linkTitle?.value ?? '';
          },
          'Assigning Title',
          this.errorHandler
        );
        return [
          ...items,
          {
            linkText: linkText,
            modalTitle: modalTitle,
            href: '',
            title: title,
            icon: 'globe',
            list: link.value.map((linkItem) => {
              let linkLabel = '';
              let title = '';
              let href = '/404';
              let fragment = '';
              let target = 'same';
              let type = '';
              safeAssign(
                () => {
                  href = this.getCaasLinkHref(linkItem);
                },
                'Assigning href',
                this.errorHandler
              );
              safeAssign(
                () => {
                  fragment = linkItem.formData.st_anchor_name?.value ?? '';
                },
                'Assigning fragment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  linkLabel = linkItem.formData.lt_link_label.value ?? '';
                },
                'Assigning linkLabel',
                this.errorHandler
              );
              safeAssign(
                () => {
                  target = this.getCaasLinkTarget(
                    linkItem.formData.lt_link_type
                  );
                },
                'Assigning target',
                this.errorHandler
              );
              safeAssign(
                () => {
                  title = linkItem.formData.lt_link_title?.value ?? '';
                },
                'Assigning title',
                this.errorHandler
              );
              safeAssign(
                () => {
                  type = linkItem.formData.lt_link_type?.value?.identifier;
                },
                'Assigning type',
                this.errorHandler
              );
              return {
                linkLabel,
                href,
                fragment,
                title,
                icon: 'globe',
                target,
                type,
              };
            }),
          },
        ];
      }
    }
    return items;
  }
  getCaasLinkTarget(linkType: CaasFormField): string {
    if (linkType.value.identifier === 'external') {
      return 'new';
    } else {
      return 'same';
    }
  }

  getCaasLinkHref(link: CaasLinksItems['value'][number]): string {
    const baseHref =
      link.formData.lt_link_type.value.identifier === 'external'
        ? link.formData.lt_link_external.value!
        : '/' +
          this.caasRoutesService.getSeoRouteByIdentifier(
            link.formData.lt_link_internal?.value?.identifier ?? '',
            this.router.config
          )?.path;

    return baseHref;
  }

  openModal(
    event: MouseEvent | KeyboardEvent,
    item: ContactItem['items'][number]
  ) {
    event.preventDefault();
    this.modalContactItem = item;
    this.showModal = true;
  }
}
